import React, { useState } from 'react';
import { Paper, Grid, Button, Tooltip } from '@material-ui/core';
import { GetApp, History, Publish } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import RBISuccessPopup from '../../RBIMessagePopup/RBISuccessPopup';
import RBIErrorPopup from '../../RBIMessagePopup/RBIErrorPopup';
import styles from './styles';
import { useTranslation } from 'react-i18next';
import RestaurantSelectionModal from './RestaurantSelectionModal';
import ShowSelectedRestaurants from './ShowSelectedRestaurants';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { OperationHourFile } from '../MassUploadFile/OperationHourFile';
import { setBaseOperationHours } from '../../../redux/actions/operationHoursActions';
import { setRestaurantsRoutineDays } from '../../../redux/actions/routineDaysActions';
import {
  getMassUploadExcelFile,
  uploadSpecialEventFile,
} from '../../../redux/actions/newOperationHoursActions';
import DownloadExcelModal from './DownloadExcelModal';
import ShowErrorMessageModal from './ShowErrorMessageModal';
import { setSelectedRestaurantList as setSelectedRestaurants } from '../../../redux/actions/restaurantsActions';
import { uploadFileOnS3 } from '../../../utils/utils';
import { toggleOperationHoursViewMode } from '../../../redux/actions/newOperationHoursActions';

const OperationRestaurantCard = () => {
  const dispatch: any = useDispatch();
  const classes = styles();
  const { t } = useTranslation();
  const selectedRestaurants = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.selectedRestaurantsList,
  );
  const [openRestaurantModal, setOpenRestaurantModal] = useState(false);
  const [successPopupModal, setSuccessPopupModal] = useState({
    isAlert: false,
    isOpen: false,
    message: '',
    shouldRedirectOnClose: false,
  });
  const [errorPopupModal, setErrorPopupModal] = useState({
    isOpen: false,
    message: '',
  });
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [uploadErrors, setErrors] = useState();
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const selectedLanguage = localStorage.getItem('selectedLanguage') || 'eng';

  const handleSelectedRestaurantsModal = () => {
    setOpenRestaurantModal(!openRestaurantModal);
  };

  const handleCloseDownloadExcelModal = () => {
    setOpenDownloadModal(false);
  };

  const handleSuccessCloseModal = () => {
    const shouldRedirect = successPopupModal.shouldRedirectOnClose;

    setSuccessPopupModal({
      isAlert: false,
      isOpen: false,
      message: '',
      shouldRedirectOnClose: false,
    });

    // Only redirect to view mode for pending process success popup
    if (shouldRedirect) {
      dispatch(toggleOperationHoursViewMode(true));
    }
  };

  const handleErrorCloseModal = () => {
    setErrorPopupModal({
      isOpen: false,
      message: '',
    });
  };

  const showMessage = (result, file = {}) => {
    if (result?.status < 300) {
      const refId = result?.data?.result?.referenceId;
      !(
        Object.keys(file).length === 0 &&
        Object.getPrototypeOf(file) === Object.prototype
      ) && uploadFileOnS3(result?.data?.result?.url, file);
      setSuccessPopupModal({
        isAlert: true,
        isOpen: true,
        message: t('operationHours.success.pendingProcess') + ' ' + refId,
        shouldRedirectOnClose: true,
      });
    } else {
      setErrorPopupModal({
        isOpen: true,
        message: t('operationHours.massUploadFile.error'),
      });
    }
  };

  const errorMessage = message => {
    setErrorPopupModal({
      isOpen: true,
      message: t(message),
    });
  };

  const handleEditionSubmit = async (JsonObject, targetFile) => {
    try {
      if (JsonObject?.setting === 'Base') {
        delete JsonObject?.setting;
        const deliveryDays = { ...JsonObject?.deliveryDays };
        const isOnlyDeliveryAvailable = JsonObject.isOnlyDeliveryAvailable;
        delete JsonObject.deliveryDays;
        delete JsonObject.isOnlyDeliveryAvailable;
        const OHResult: any =
          !isOnlyDeliveryAvailable &&
          (await dispatch(setBaseOperationHours('massUpload', JsonObject)));
        if (deliveryDays && deliveryDays?.rows?.length) {
          const RDResult = await dispatch(
            setRestaurantsRoutineDays(deliveryDays),
          );
          isOnlyDeliveryAvailable && showMessage(RDResult);
        }
        !isOnlyDeliveryAvailable && showMessage(OHResult, targetFile.files[0]);
      } else {
        delete JsonObject?.setting;
        const SEResult = await dispatch(uploadSpecialEventFile(JsonObject));
        showMessage(SEResult, targetFile.files[0]);
      }
    } catch (error) {
      const errorCode = error?.response?.data?.error_code;
      const isMassUploadError =
        error?.response?.data?.error_description?.length;

      if (errorCode === 'INTERNAL_SERVER_ERROR') {
        setOpenErrorModal(true);
        setErrors(error?.response?.data);
        // Validation error with mass upload error is already handled in special event response
      } else if (errorCode !== 'VALIDATION_ERROR' || !isMassUploadError) {
        setErrorPopupModal({
          isOpen: true,
          message: t('operationHours.massUpload.fail'),
        });
      }
    }
  };

  const selectFile = e => {
    e.preventDefault();
    const name = e?.target?.files[0]?.name;
    const re = /(\.xlsx|\.xls)$/i;
    if (!re.exec(name)) {
      setErrorPopupModal({
        isOpen: true,
        message: t('operationHours.fileFormat.message'),
      });
    } else {
      OperationHourFile(
        e.target,
        selectedBrand,
        handleEditionSubmit,
        errorMessage,
      );
    }
  };

  const downloadExcelFile = type => {
    dispatch(
      getMassUploadExcelFile(
        selectedBrand,
        type,
        selectedLanguage,
        encodeURIComponent(
          t(`operationHours.${type.toLowerCase()}`, type) + '.xlsx',
        ),
      ),
    );
  };

  return (
    <Paper className={classes.paperStyle}>
      <Grid container spacing={0}>
        <Grid item sm={7} xs={12} lg={7}>
          <ShowSelectedRestaurants
            values={selectedRestaurants}
            onUpdate={values => dispatch(setSelectedRestaurants(values))}
          />
        </Grid>
        <Grid
          container
          sm={5}
          xs={12}
          lg={5}
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Grid container item md={2} lg={5} justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.downloadButton}
              onClick={handleSelectedRestaurantsModal}
            >
              {t('operationHours.selectRestaurants')}
            </Button>
          </Grid>
          <Grid container item md={2} lg={2} justifyContent="center">
            <Tooltip title={t('operationHours.uploadFile') || ''}>
              <Button
                variant="outlined"
                component="label"
                className={classes.gridIconButton}
              >
                <Publish />
                <input
                  data-testid="operation-hours-upload-file"
                  type="file"
                  hidden
                  name="upload"
                  id="upload"
                  onChange={selectFile}
                  onClick={event => {
                    (event.target as HTMLInputElement).value = '';
                  }}
                  accept=".xls,.xlsx"
                />
              </Button>
            </Tooltip>
          </Grid>
          <Grid container item md={2} lg={2} justifyContent="center">
            <Tooltip title={t('operationHours.downloadFile') || ''}>
              <Button
                data-testid="operation-restaurant-card-download-button"
                variant="outlined"
                className={classes.gridIconButton}
                onClick={() => setOpenDownloadModal(true)}
              >
                <GetApp />
              </Button>
            </Tooltip>
          </Grid>
          <Grid container item md={2} lg={2} justifyContent="center">
            <Tooltip title={t('operationHours.viewHistory') || ''}>
              <Button
                component={Link}
                to={`/massUploadHistory`}
                variant="outlined"
                className={classes.gridIconButton}
              >
                <History />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
      {openRestaurantModal && (
        <RestaurantSelectionModal
          openRestaurantModal={openRestaurantModal}
          handleSelectedRestaurantsModal={handleSelectedRestaurantsModal}
        />
      )}
      {openDownloadModal && (
        <DownloadExcelModal
          isOpen={openDownloadModal}
          onClose={handleCloseDownloadExcelModal}
          onSubmit={downloadExcelFile}
        />
      )}
      {openErrorModal && (
        <ShowErrorMessageModal
          isOpen={openErrorModal}
          onClose={() => setOpenErrorModal(false)}
          errors={uploadErrors}
        />
      )}
      {successPopupModal.isOpen && (
        <RBISuccessPopup
          isOpen={successPopupModal.isOpen}
          isAlert={successPopupModal.isAlert}
          closeModal={handleSuccessCloseModal}
          message={successPopupModal.message}
        />
      )}
      {errorPopupModal.isOpen && (
        <RBIErrorPopup
          isOpen={errorPopupModal.isOpen}
          closeModal={handleErrorCloseModal}
          message={errorPopupModal.message}
          messageHeader={t('operationHours.error.headerText')}
          footerText={t('app.common.close')}
        />
      )}
    </Paper>
  );
};

export default OperationRestaurantCard;
